import React, { useState, useEffect, useCallback } from "react";
import {
  Users,
  ChevronLeft,
  Trash2,
  Edit,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import Modal from "../../../Modal/Modal";
import TeamMembersList from "./TeamMembersList/TeamMembersList";
import TeamColorPicker from "../../../TeamColorPicker/TeamColorPicker";
import "./ClanEventTeamsTab.css";

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;
// Total display time: 4s visible + 1s fade = 5000ms
const MESSAGE_TIMEOUT_MS = 5000;

function ClanEventTeamsTab({ clanId, event, goBackToList }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [apiMessage, setApiMessage] = useState({ type: null, text: null });

  // NEW STATE: A simple counter/toggle to signal to *all* TeamMembersList components
  // that the global pool of eligible members might have changed.
  const [memberListChanged, setMemberListChanged] = useState(0);

  const [isMessageFading, setIsMessageFading] = useState(false);

  const [expandedTeamIds, setExpandedTeamIds] = useState(new Set());

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [teamToEdit, setTeamToEdit] = useState(null);
  const [teamToDelete, setTeamToDelete] = useState(null);

  const initialNewTeamData = {
    name: "",
    color: "#000000",
  };
  const [createFormData, setCreateFormData] = useState(initialNewTeamData);
  const [editFormData, setEditFormData] = useState(initialNewTeamData);
  const [formError, setFormError] = useState(null);

  const fetchTeams = useCallback(async () => {
    setTeamsLoading(true);
    setLoading(true);
    // FIXED: This was removed in the previous step and should remain removed.
    setTeamToEdit(null);

    const url = `${API_BASE_URL}/clan/${clanId}/events/${event.id}/teams/`;

    try {
      const response = await fetch(url, { credentials: "include" });
      const data = await response.json();

      if (!response.ok) {
        setApiMessage({
          type: "error",
          text: data.error || "Failed to fetch teams.",
        });
        setTeams([]);
      } else {
        setTeams(data.teams || []);
      }
    } catch (error) {
      setApiMessage({ type: "error", text: "Network error fetching teams." });
      console.error("Team fetch error:", error);
    } finally {
      setTeamsLoading(false);
      setLoading(false);
    }
  }, [clanId, event.id]);

  // Initial Load: Only depends on IDs and fetchTeams.
  // It does *not* depend on the `loading` state, preventing the loop.
  useEffect(() => {
    if (clanId && event.id) {
      fetchTeams();
    }
  }, [clanId, event.id, fetchTeams]);

  // Effect to auto-clear the API message with a fade
  useEffect(() => {
    let fadeTimer;
    let clearTimer;

    if (apiMessage.text) {
      // Only auto-clear if it's NOT a modal-specific form error
      // Note: `formError` is local to the modal submission, not part of apiMessage state.
      // Since the handleTeamSubmit function sets both apiMessage (on success/fail) and formError (on fail),
      // we only want to clear if the message wasn't just set for a modal validation issue.
      // The current logic to prevent clearing if `formError` is present in the scope of this file is fine for now.
      if (!formError) {
        fadeTimer = setTimeout(() => {
          setIsMessageFading(true);
        }, MESSAGE_TIMEOUT_MS - 1000);

        clearTimer = setTimeout(() => {
          setApiMessage({ type: null, text: null });
          setIsMessageFading(false);
        }, MESSAGE_TIMEOUT_MS);
      }
    }

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(clearTimer);
      // Ensure fading state is reset if component unmounts or message changes quickly
      if (apiMessage.text) {
        setIsMessageFading(false);
      }
    };
  }, [apiMessage.text, formError]);

  // ---------------------------------------------------------------------
  // NEW SYNCHRONIZATION LOGIC
  // ---------------------------------------------------------------------
  // This handler is called by the child after a successful member add/remove.
  const updateTeamDataAndSync = useCallback(() => {
    fetchTeams();

    setMemberListChanged((prev) => prev + 1);
  }, [fetchTeams]);
  // ---------------------------------------------------------------------

  const handleCreateTeamClick = () => {
    setCreateFormData(initialNewTeamData);
    setFormError(null);
    setApiMessage({ type: null, text: null });
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setFormError(null);
    setCreateFormData(initialNewTeamData);
  };

  const handleEditTeamClick = (team) => {
    setTeamToEdit({ ...team });
    setEditFormData({
      name: team.name,
      color: team.color,
    });
    setFormError(null);
    setApiMessage({ type: null, text: null });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setTeamToEdit(null);
    setFormError(null);
    setEditFormData(initialNewTeamData);
  };

  // Row Expansion Handler
  const handleManageMembersClick = (teamId) => {
    setApiMessage({ type: null, text: null });
    const nextExpandedIds = new Set(expandedTeamIds);
    if (nextExpandedIds.has(teamId)) {
      nextExpandedIds.delete(teamId);
    } else {
      nextExpandedIds.add(teamId);
    }
    setExpandedTeamIds(nextExpandedIds);
  };

  const closeDeleteModal = () => {
    setTeamToDelete(null);
  };

  const handleCreateFormChange = (e) => {
    const { name, value } = e.target;
    setCreateFormData((prev) => ({ ...prev, [name]: value }));
    setFormError(null);
    setApiMessage({ type: null, text: null });
  };

  const handleCreateColorChange = (newHexColor) => {
    setCreateFormData((prev) => ({ ...prev, color: newHexColor }));
    setFormError(null);
    setApiMessage({ type: null, text: null });
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
    setFormError(null);
    setApiMessage({ type: null, text: null });
  };

  const handleEditColorChange = (newHexColor) => {
    setEditFormData((prev) => ({ ...prev, color: newHexColor }));
    setFormError(null);
    setApiMessage({ type: null, text: null });
  };

  const handleTeamSubmit = async (mode, teamId = null, formDataToUse) => {
    if (loading) return;
    setLoading(true);
    setFormError(null);

    if (!formDataToUse.name.trim()) {
      setFormError("Team name is required.");
      setLoading(false);
      return false;
    }

    const isEdit = mode === "edit";
    const url = isEdit
      ? `${API_BASE_URL}/clan/${clanId}/events/${event.id}/teams/${teamId}`
      : `${API_BASE_URL}/clan/${clanId}/events/${event.id}/teams/`;

    const method = isEdit ? "PUT" : "POST";
    const requestBody = {
      name: formDataToUse.name,
      color: formDataToUse.color,
    };

    try {
      const response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        setApiMessage({
          type: "error",
          text: data.error || `Failed to ${isEdit ? "update" : "create"} team.`,
        });
        setFormError(
          data.error || `Failed to ${isEdit ? "update" : "create"} team.`
        );
        return false;
      } else {
        const successMessage = isEdit
          ? `Team '${data.team.name}' updated successfully.`
          : `Team '${data.team.name}' created successfully!`;

        setApiMessage({ type: "success", text: successMessage });

        if (isEdit) {
          closeEditModal();
        } else {
          closeCreateModal();
        }

        fetchTeams();
        return true;
      }
    } catch (error) {
      setFormError("Network error. Failed to connect to the server.");
      console.error(`Team ${mode} network error:`, error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const executeCreateTeam = () => {
    handleTeamSubmit("create", null, createFormData);
  };

  const executeEditTeam = () => {
    handleTeamSubmit("edit", teamToEdit?.id, editFormData);
  };

  const executeDeleteTeam = async () => {
    if (!teamToDelete || loading) return;

    setLoading(true);
    setApiMessage({ type: null, text: null });

    const teamId = teamToDelete.id;
    const teamName = teamToDelete.name;
    const url = `${API_BASE_URL}/clan/${clanId}/events/${event.id}/teams/${teamId}`;

    try {
      const response = await fetch(url, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json();
        setApiMessage({
          type: "error",
          text: data.error || "Failed to delete team.",
        });
      } else {
        setApiMessage({
          type: "success",
          text: `Team '${teamName}' deleted successfully.`,
        });
        // closeDeleteModal is called *after* setting the message
        closeDeleteModal();
        fetchTeams();
      }
    } catch (error) {
      setApiMessage({
        type: "error",
        text: "Network error. Failed to connect to the server.",
      });
      console.error("Team deletion network error:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderTeamDetailsForm = ({
    mode,
    onChange,
    onColorChange,
    formData,
  }) => {
    const currentApiMessage = formError;
    const messageType = formError ? "error" : null;

    return (
      <div className="team-details-form-container">
        {currentApiMessage && (
          <div className={`form-message ${messageType}`}>
            {currentApiMessage}
          </div>
        )}

        <form
          className="team-details-form"
          onSubmit={(e) => e.preventDefault()}
        >
          <div className="form-group">
            <label className="modal-form-label" htmlFor="name">
              Team Name
            </label>
            <input
              id="name"
              className="modal-form-field"
              type="text"
              name="name"
              value={formData.name}
              onChange={onChange}
              disabled={loading}
              required
            />
          </div>
          <div className="form-group">
            <TeamColorPicker
              label="Team Color"
              initialColor={formData.color}
              onColorChange={onColorChange}
            />
          </div>
        </form>
      </div>
    );
  };

  const isCreateDisabled = loading || !createFormData.name.trim();
  const isEditDisabled = loading || !editFormData.name.trim();

  const renderTeamRows = () => {
    if (teamsLoading) {
      return (
        <tr>
          <td className="table-placeholder-cell" colSpan="4">
            <small>Loading teams...</small>
          </td>
        </tr>
      );
    }

    if (teams.length === 0) {
      return (
        <tr>
          <td className="table-placeholder-cell" colSpan="4">
            <p className="no-teams-message">
              No teams created for this event. Click "Create New Team" to start!
            </p>
          </td>
        </tr>
      );
    }

    return teams.map((team) => {
      const isExpanded = expandedTeamIds.has(team.id);
      const MembersIcon = isExpanded ? ChevronUp : ChevronDown;

      return (
        <React.Fragment key={team.id}>
          <tr className={isExpanded ? "team-row-active" : ""}>
            <td
              className="team-name-cell"
              data-label="Name"
              style={{
                "--team-border-color": team.color || "#cccccc",
              }}
            >
              <span className="team-name-text">{team.name}</span>

              <span className="team-size-pill-mobile">
                {team.member_count || 0} Members
              </span>
            </td>
            <td className="team-size-cell" data-label="Size">
              {team.member_count || 0} Members
            </td>
            <td data-label="Manage">
              <button
                className="btn-small btn-secondary team-members-toggle-btn"
                onClick={() => handleManageMembersClick(team.id)}
                disabled={loading}
                aria-expanded={isExpanded}
              >
                <Users size={16} style={{ marginRight: "5px" }} />
                Members
                <MembersIcon size={16} style={{ marginLeft: "5px" }} />
              </button>
            </td>
            <td className="team-actions-cell" data-label="Actions">
              <button
                className="btn-icon-small"
                onClick={() => handleEditTeamClick(team)}
                disabled={loading}
                title="Edit Team Details"
              >
                <Edit size={16} />
              </button>
              <button
                className="btn-icon-small btn-danger"
                onClick={() => setTeamToDelete(team)}
                disabled={loading}
                title="Delete Team"
              >
                <Trash2 size={16} />
              </button>
            </td>
          </tr>
          {isExpanded && (
            <tr className="expanded-members-row">
              <td colSpan="4">
                <div className="members-list-wrapper">
                  <TeamMembersList
                    clanId={clanId}
                    eventId={event.id}
                    team={team}
                    loading={loading}
                    setLoading={setLoading}
                    apiMessage={apiMessage}
                    setApiMessage={setApiMessage}
                    onMemberChangeComplete={updateTeamDataAndSync}
                    memberListChanged={memberListChanged}
                  />
                </div>
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="clan-event-teams-tab-container">
      <div className="event-sub-nav">
        <button
          className="sub-nav-button"
          onClick={goBackToList}
          disabled={loading}
        >
          <ChevronLeft size={18} style={{ marginRight: "5px" }} /> Back to
          Events
        </button>
        <span className="current-context-display">
          Managing:&nbsp;
          <strong className="current-view-name">{"Teams List"}</strong>
        </span>
      </div>

      <div className="team-tab-content-area">
        <div className="team-list-controls">
          {apiMessage.text &&
            !teamToDelete &&
            !showCreateModal &&
            !showEditModal && (
              <div
                className={`event-team-global-message ${apiMessage.type} ${
                  isMessageFading ? "fading-out" : ""
                }`}
              >
                {apiMessage.text}
              </div>
            )}
          <button
            className="btn-large btn-primary push-right-auto"
            disabled={loading || teamsLoading}
            onClick={handleCreateTeamClick}
          >
            Create New Team
          </button>
        </div>
        <div className="team-list-placeholder">
          <table className="teams-placeholder-table">
            <thead>
              <tr className="table-header-row">
                <th className="table-header">Team Name</th>
                <th className="table-header">Size</th>
                <th className="table-header action-col">Manage</th>
                <th className="table-header action-col">Edit / Delete</th>
              </tr>
            </thead>
            <tbody>{renderTeamRows()}</tbody>
          </table>
        </div>
      </div>
      <Modal
        isOpen={showCreateModal}
        title="Create New Team"
        message={
          <div className="modal-form-wrapper">
            {renderTeamDetailsForm({
              mode: "create",
              onChange: handleCreateFormChange,
              onColorChange: handleCreateColorChange,
              formData: createFormData,
            })}
          </div>
        }
        onConfirm={executeCreateTeam}
        onCancel={closeCreateModal}
        confirmButtonText={loading ? "Creating..." : "Create Team"}
        confirmButtonClassName={
          isCreateDisabled ? "btn-primary btn-disabled" : "btn-primary"
        }
      />
      <Modal
        isOpen={showEditModal}
        title={`Edit Team: ${teamToEdit?.name || "Loading..."}`}
        message={
          teamToEdit ? (
            <div className="modal-form-wrapper">
              {renderTeamDetailsForm({
                mode: "edit",
                onChange: handleEditFormChange,
                onColorChange: handleEditColorChange,
                formData: editFormData,
              })}
            </div>
          ) : (
            <p>Loading team data...</p>
          )
        }
        onConfirm={executeEditTeam}
        onCancel={closeEditModal}
        confirmButtonText={loading ? "Updating..." : "Update Team"}
        confirmButtonClassName={
          isEditDisabled ? "btn-primary btn-disabled" : "btn-primary"
        }
      />
      {teamToDelete && (
        <Modal
          isOpen={!!teamToDelete}
          title="Confirm Team Deletion"
          message={
            <div className="delete-confirm-message">
              <p>
                Are you sure you want to <strong>permanently delete</strong> the
                team:
              </p>
              <p className="team-name-to-delete">"{teamToDelete.name}"</p>
              <p>
                This will also remove all members from the team. This action
                cannot be undone.
              </p>
            </div>
          }
          onConfirm={executeDeleteTeam}
          onCancel={closeDeleteModal}
          confirmButtonText={loading ? "Processing..." : "Delete Team"}
          confirmButtonClassName={
            loading ? "btn-danger btn-disabled" : "btn-danger"
          }
          cancelButtonText="Cancel"
        />
      )}
    </div>
  );
}

export default ClanEventTeamsTab;
